import { useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useUnreadEvents } from '../context/UnreadEventsContext';
import { getEventPriority } from '../utils/clinicalEvents';
import { calculateTimePassed } from '../utils/CalculateTimePassed';

const CRITICAL_EVENT_TYPES = ['FALLDOWN', 'SOS', 'SHOCK', 'GEO_FENCE', 'OXYGEN', 'TEMPERATURE'];

/**
 * Custom Hook: useHeaderNotifications
 * Lógica pura desacoplada (Zero JSX) para la campana de notificaciones del Header.
 * Consume el UnreadEventsContext (alimentado por WebSockets y polling) y formatea
 * las alertas clínicas en tiempo real con niveles de urgencia.
 */
export const useHeaderNotifications = () => {
	const navigate = useNavigate();
	const { allEvents = [], hasUnread, refresh } = useUnreadEvents();

	const unreadEvents = useMemo(() => {
		return Array.isArray(allEvents) ? allEvents : [];
	}, [allEvents]);

	const unreadCount = unreadEvents.length;

	const hasCritical = useMemo(() => {
		return unreadEvents.some(ev => {
			const type = ev?.name || ev?.type || '';
			return CRITICAL_EVENT_TYPES.includes(type) || getEventPriority(type) <= 4;
		});
	}, [unreadEvents]);

	// Últimos 5 eventos clínicos formateados
	const recentEvents = useMemo(() => {
		return unreadEvents.slice(0, 5).map(ev => {
			const type = ev?.name || ev?.type || 'EVENT';
			const priority = getEventPriority(type);
			const isCritical = priority <= 4 || CRITICAL_EVENT_TYPES.includes(type);

			const patientName = ev?.Patient?.name
				? `${ev.Patient.name} ${ev.Patient.lastname || ''}`.trim()
				: ev?.patientName || ev?.patient?.name || '';

			const patientId = ev?.Patient?.patientId || ev?.patientId || ev?.patient?.patientId || null;

			return {
				id: ev?.eventId || ev?.id || Math.random().toString(),
				type,
				title: ev?.title || type,
				patientName,
				patientId,
				timeAgo: ev?.createdAt ? calculateTimePassed(ev.createdAt) : '',
				priority,
				isCritical,
			};
		});
	}, [unreadEvents]);

	const navigateToEvents = useCallback(
		onClose => {
			onClose?.();
			navigate('/events');
		},
		[navigate],
	);

	const navigateToPatient = useCallback(
		(patientId, onClose) => {
			onClose?.();
			if (patientId) {
				navigate(`/patients/${patientId}`);
			} else {
				navigate('/events');
			}
		},
		[navigate],
	);

	return {
		unreadCount,
		hasUnread: hasUnread || unreadCount > 0,
		hasCritical,
		recentEvents,
		refreshEvents: refresh,
		navigateToEvents,
		navigateToPatient,
	};
};
